import React, { Fragment } from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';

import Header from '../../components/Header';
import Load from '../../components/Load';
import PostRouter from '../../components/PostRouter';
import { POST_POR_ID, PostPorIdData, PostPorIdVars } from '../../services/postService';

/**
 * Página pública de post único: `/post/:postId`.
 *
 * Reusa o mesmo caminho que `Userpage.tsx` já percorre (`postPorId` →
 * `PostRouter`), sem o andaime de perfil de atleta em volta — é o destino
 * que o link de compartilhamento (PRD AC-6/AC-7) precisava ter.
 *
 * MONTA `<Header />`, que traz cabeçalho + menu + rodapé juntos.
 *
 * A versão anterior o omitia, com o argumento de que a página é pública
 * (`isPublic: true` em `routes.tsx`) e portanto isenta da regra 9 de
 * `ai/architecture.core.md`. O argumento não se sustenta: a regra 9 obriga
 * rotas PROTEGIDAS a montar o shell — ela não proíbe as públicas de fazê-lo,
 * e estar isento de uma obrigação não é razão para omitir. O resultado era um
 * usuário logado chegando pelo link compartilhado e caindo numa página sem
 * cabeçalho, rodapé nem navegação, sem caminho de volta para o app (achado de
 * QA manual do owner, 2026-08-31).
 *
 * Incondicional, espelhando `pages/public/LeagueProfilePage.tsx:278,298` — a
 * outra página pública do repo, que já monta `<Header />` sempre. O `Header`
 * degrada sozinho para visitante anônimo: lê `localStorage` com fallback nulo
 * (`|| 'null'`) e `usuarioId = user?.id ?? ''`.
 */
const PostPage: React.FC = () => {
    const { postId } = useParams<{ postId: string }>();

    const { data, loading, error } = useQuery<PostPorIdData, PostPorIdVars>(POST_POR_ID, {
        variables: { id: postId || '' },
        skip: !postId,
    });

    const post = data?.postPorId ?? null;

    return (
        <Fragment>
            <Header />

            <div className="main-content right-chat-active">
                <div className="middle-sidebar-bottom">
                    <div className="middle-sidebar-left pe-0">
                        <div className="row justify-content-center">
                            <div className="col-xl-8 col-xxl-6 col-lg-8">
                                {loading && <Load />}

                                {!loading && error && (
                                    <div className="alert alert-danger" role="alert">
                                        Erro ao carregar o post.
                                    </div>
                                )}

                                {!loading && !error && !post && (
                                    <div className="alert alert-warning" role="alert">
                                        Post não encontrado.
                                    </div>
                                )}

                                {!loading && !error && post && (
                                    <PostRouter key={post.id} post={post} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default PostPage;
